import { last } from "lodash";
import { useEffect, useState, useCallback } from "react";
import Iframe from "react-iframe";
import { useApolloClient } from "@apollo/client";

import { GET_DISTRIBUTION_URL } from "./gql";

import NotFoundImg from "/src/assets/file_not_found.jpeg";
import { APP_V2_APP_EVENTS, logError } from "src/appV2/lib/analytics";
import { UrlDisplay } from "@src/appV2/FileDisplay/UrlDisplay";
import { ismsWordTypes } from "@src/appV2/FileDisplay/getDocumentExtension";
import { IconButton, Stack } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadDocument } from "@src/appV2/lib/utils";
import { PageActions } from "@src/appV2/WorkerDocuments/types/common";
import { showErrorToast } from "@src/appV2/lib/Notifications";

const ImageTypes = [
  "apng",
  "bmp",
  "gif",
  "ico",
  "cur",
  "jpg",
  "jpeg",
  "jfif",
  "pjpeg",
  "pjp",
  "png",
  "svg",
  "webp",
];

function DocumentPreview({ doc, width, height, isPreviewImage = false, setImagePath, shiftId }) {
  const [loading, setLoading] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [url, setUrl] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const client = useApolloClient();

  const fetchFile = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { getDistributionUrl: { distributionUrl = "" } = {} },
      } = await client.query({
        query: GET_DISTRIBUTION_URL,
        variables: { docId: doc._id, shiftId },
        fetchPolicy: "network-only",
      });

      // This is the format of the distributionUrl.
      // https://hash.cloudfront.net/id/id/file-name-hash.png?Expires=Number&Key-Pair-Id=Key&Signature=Signature
      let extension: string = last(distributionUrl.toLowerCase().split("."))!;
      extension = extension.split("?")[0];
      setUrl(distributionUrl);
      setIsImage(ImageTypes.includes(extension));
    } catch (error) {
      logError("Error fetching file", {
        error,
        metadata: {
          docId: doc._id,
          shiftId,
        },
      });
      setUrl("");
    }
    setLoading(false);
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc._id]);

  useEffect(() => {
    if (!doc._id) {
      return;
    }
    void fetchFile();
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc._id]);

  const handleImageClick = () => {
    if (isPreviewImage) {
      setImagePath(url);
    }
  };

  const handleImageError = () => {
    setUrl(NotFoundImg);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadDocument(url, "document");
    } catch (error) {
      logError(APP_V2_APP_EVENTS.WORKER_DOCUMENTS_ACTION_FAILURE, {
        error,
        metadata: {
          action: PageActions.DOWNLOAD,
        },
      });
      showErrorToast("Failed to download document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return <div>loading...</div>;
  } else if (isImage) {
    return (
      <>
        <Stack direction="row" justifyContent="flex-end">
          <IconButton
            aria-label="download"
            sx={{
              color: (theme) => theme.palette.grey[700],
            }}
            disabled={isDownloading}
            onClick={handleDownload}
          >
            <DownloadIcon />
          </IconButton>
        </Stack>
        {/* FIXME: Automatically disabled during ESLint config update */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <img
          className={isPreviewImage ? "preview-img" : ""}
          style={{ maxWidth: width || "100%", maxHeight: height || "100%" }}
          src={url}
          onError={handleImageError}
          alt="preview"
          title={isPreviewImage ? "Click on the image to rotate, zoom it" : ""}
          onClick={handleImageClick}
          data-testid="document-preview-image"
        />
      </>
    );
  } else if (ismsWordTypes(url)) {
    return <UrlDisplay url={url} />;
  } else {
    return (
      <Iframe
        url={url}
        width={width || "100%"}
        height={height || "100%"}
        /* @ts-expect-error FIXME this wrong type */
        display="initial"
        position="relative"
      />
    );
  }
}

export { DocumentPreview };
